import { useEffect, useRef, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router';
import { useAppAuth } from '../contexts';
import { redirectToAppLogin } from '../utils/appLogin';
import {
  clearTrainingReturnTo,
  peekTrainingReturnTo,
  resolveTrainingExternalReturnTo,
  storeTrainingReturnTo,
} from '../utils/trainingAuthSync';

/**
 * Redirects authenticated users back to www.ept.ai with a one-time token hash.
 * JWT stays in the URL fragment (not sent to servers) and is stored in
 * sessionStorage by the Training Room site.
 */
export default function TrainingAuthSync(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, getIdTokenClaims } = useAppAuth();
  const [status, setStatus] = useState('Preparing Training Room sign-in...');
  const [error, setError] = useState<string | null>(null);
  const handoffStartedRef = useRef(false);
  const loginStartedRef = useRef(false);

  const returnToParam = searchParams.get('returnTo');

  useEffect(() => {
    if (isLoading) {
      setStatus('Checking your session...');
      return;
    }

    const returnTo = resolveTrainingExternalReturnTo(
      returnToParam || peekTrainingReturnTo()
    );

    if (!isAuthenticated) {
      if (loginStartedRef.current) {
        return;
      }
      loginStartedRef.current = true;
      setStatus('Redirecting to sign in...');
      // The external returnTo cannot ride in Auth0 appState (App.tsx only
      // restores same-origin paths), so it is stashed first. sessionStorage
      // survives the full-page assign to /login, and redirectToAppLogin also
      // saves `/training-auth-sync?returnTo=...` as a same-origin fallback.
      storeTrainingReturnTo(returnTo);
      if (!redirectToAppLogin()) {
        loginStartedRef.current = false;
        setError('Unable to start sign-in. Please try again.');
      }
      return;
    }

    if (handoffStartedRef.current) {
      return;
    }
    handoffStartedRef.current = true;
    setStatus('Sending you back to Training Room...');

    // Once handoff starts, always finish the redirect. Effect cleanup must not
    // abort navigation after returnTo was captured (or users hang with no Retry).
    const returnToForHandoff = returnTo;
    void getIdTokenClaims()
      .then(claims => {
        clearTrainingReturnTo();
        if (!claims?.__raw) {
          window.location.replace(returnToForHandoff);
          return;
        }

        const target = new URL(returnToForHandoff);
        target.hash = `ept_auth_token=${encodeURIComponent(claims.__raw)}`;
        window.location.replace(target.toString());
      })
      .catch(() => {
        clearTrainingReturnTo();
        window.location.replace(returnToForHandoff);
      });
  }, [
    getIdTokenClaims,
    isAuthenticated,
    isLoading,
    returnToParam,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '12px',
        minHeight: '100vh',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <div>{error ?? status}</div>
      {error ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            loginStartedRef.current = false;
            handoffStartedRef.current = false;
            window.location.reload();
          }}
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
